import { type Prompt, pageUrl, pagesIn, rangesAsText } from '../../api/exams'
import { Preview } from '../../ui/preview/Preview'

/**
 * What a task asks, drawn.
 *
 * Three shapes, and the interesting one is the first.
 *
 * **Pages of a document are drawn as pictures, one per page.** Not the PDF in a frame: a frame at a
 * page anchor shows every page around the one that was named, and on an iPad Safari renders an
 * embedded PDF without scrolling it — so a three-page question would be its first page and no more.
 * Each named page is fetched as an image, which also means the question scrolls like anything else on
 * the screen and can stand in half the width beside the answer.
 *
 * **A written question is drawn, not shown as its markup.** It is a text somebody is reading, so it
 * goes through the same renderer as every other marked-up text in MyLib.
 *
 * **A card set shows every card's front.** Not one at a time, moderated — that belongs to the
 * learning round, and this is deliberately not one: every card stands at once, the same way several
 * pages of a document already do, and the actual writing happens beside each one on the answer side.
 *
 * Where the document has gone — the trash, or for good — the task stands and says so. A question that
 * quietly disappeared with its source would be worse than one that admits it cannot be shown.
 */
export interface QuestionProps {
  readonly prompt: Prompt
  /** Names the region for a screen reader: "Task 1". */
  readonly label: string
}

export function Question({ prompt, label }: QuestionProps) {
  if (prompt.kind === 'written') {
    return (
      <div className="question" aria-label={label}>
        <Preview text={prompt.text ?? ''} format={prompt.markup ?? 'text/markdown'} label={label} />
      </div>
    )
  }

  if (prompt.kind === 'cardset') {
    const cards = prompt.cards ?? []
    if (cards.length === 0) {
      return (
        <p className="question__gone" aria-label={label}>
          This set names no card any more.
        </p>
      )
    }
    return (
      <ol className="question__cards" aria-label={label}>
        {cards.map((card, index) => (
          <li key={card.id} className="question__card">
            {card.gone ? (
              <span className="question__gone">A card that is no longer there.</span>
            ) : (
              <Preview
                text={card.front}
                format={card.frontFormat}
                label={`Card ${String(index + 1)} of ${String(cards.length)}`}
              />
            )}
          </li>
        ))}
      </ol>
    )
  }

  const documentId = prompt.documentId ?? null
  const pages = pagesIn(prompt.pages ?? [])

  if (documentId === null || pages.length === 0) {
    return (
      <p className="question__gone" aria-label={label}>
        This task names no page any more.
      </p>
    )
  }

  return (
    <div className="question" aria-label={label}>
      <p className="question__source">
        {prompt.documentTitle ?? 'A document that is no longer in the library'}
        <span className="question__pages">
          {pages.length === 1 ? 'Page ' : 'Pages '}
          {rangesAsText(prompt.pages ?? [])}
        </span>
      </p>
      <ol className="question__sheets">
        {pages.map((page) => (
          <li className="question__sheet" key={page}>
            <img
              className="question__page"
              src={pageUrl(documentId, page)}
              alt={`Page ${String(page)} of ${prompt.documentTitle ?? 'the document'}`}
              loading="lazy"
            />
            <span className="question__number">{page}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
